import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Search, Briefcase, FileText, ArrowRight } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  {
    label: "Services",
    to: "/services",
    children: [
      { label: "Permanent Recruitment", to: "/services/permanent-recruitment" },
      { label: "Contract Staffing", to: "/services/contract-staffing" },
      { label: "Recruitment Process Outsourcing (RPO)", to: "/services/rpo" },
      { label: "Payroll Management", to: "/services/payroll-management" },
    ],
  },
  {
    label: "Find Jobs",
    to: "/careers/openings",
    icon: Briefcase,
    footer: "Your next opportunity could start here",
    children: [
      { label: "Browse Jobs", to: "/careers/openings", icon: Search, sub: "Explore current openings" },
      { label: "Upload CV", to: "/careers/apply", icon: FileText, sub: "Let opportunities find you" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_8px_30px_-18px_rgba(14,15,19,0.25)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="group" data-testid="nav-logo">
          <span className="grid grid-cols-[auto_auto] items-center gap-x-3">
            <img src="/genspire-g-mark.png" alt="Genspire logo" className="h-[27px] sm:h-[30px] w-auto transition-transform duration-500 group-hover:scale-[1.03]" />
            <span className="font-wordmark font-bold text-[23px] sm:text-[25px] text-brand-red tracking-tight leading-none">Genspire</span>
            <span className="col-start-2 mt-[5px] -mr-[0.095em] sm:-mr-[0.112em] font-mono text-[8px] sm:text-[8.5px] uppercase tracking-[0.095em] sm:tracking-[0.112em] text-slate-500 leading-none">Partner in Progress</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="nav-links">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group" data-testid={`nav-${slug(item.label)}-dropdown`}>
                <NavLink to={item.to} data-testid={`nav-link-${slug(item.label)}`}
                  className={({ isActive }) =>
                    `flex h-10 items-center gap-1.5 px-3.5 text-[13.5px] font-medium leading-none tracking-wide transition-colors duration-300 ${isActive ? "text-brand-red" : "text-slate-600 hover:text-brand-red"}`
                  }>
                  {item.icon && <item.icon size={14} strokeWidth={2.2} className="shrink-0" />}
                  {item.label}
                  <ChevronDown size={13} className="translate-y-px shrink-0 transition-transform duration-300 group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className={`${item.label === "Services" ? "w-80" : "w-72"} bg-white border border-slate-200 shadow-[0_28px_60px_-30px_rgba(14,15,19,0.35)] py-2`}>
                    {item.children.map((c) =>
                      c.icon ? (
                        <Link key={c.to} to={c.to} data-testid={`nav-dropdown-${slug(c.label)}`}
                          className="group/item flex items-start gap-3.5 px-5 py-3 transition-colors">
                          <c.icon size={16} strokeWidth={1.75} className="mt-0.5 shrink-0 text-slate-400 transition-colors duration-300 group-hover/item:text-brand-red" />
                          <span className="flex flex-col">
                            <span className="text-[13px] font-medium text-slate-700 transition-colors duration-300 group-hover/item:text-brand-red">{c.label}</span>
                            <span className="mt-0.5 text-[11px] leading-snug text-slate-400">{c.sub}</span>
                          </span>
                        </Link>
                      ) : (
                        <Link key={c.to} to={c.to} data-testid={`nav-dropdown-${slug(c.label)}`}
                          className="group/item flex items-center justify-between gap-4 px-5 py-3 text-[13px] text-slate-600 hover:text-brand-red transition-colors">
                          {c.label}
                          <span className="h-px w-0 bg-brand-red transition-all duration-300 group-hover/item:w-5" />
                        </Link>
                      )
                    )}
                    {item.footer && (
                      <div className="mt-1.5 border-t border-slate-100 px-5 pt-3.5 pb-1.5" data-testid="nav-dropdown-footer">
                        <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          {item.footer}
                          <ArrowRight size={11} className="text-brand-red" />
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.label} to={item.to} data-testid={`nav-link-${slug(item.label)}`}
                className={({ isActive }) =>
                  `flex h-10 items-center px-3.5 text-[13.5px] font-medium leading-none tracking-wide transition-colors duration-300 ${isActive ? "text-brand-red" : "text-slate-600 hover:text-brand-red"}`
                }>
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-slate-800" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.label} className="flex flex-col gap-4">
                    <Link to={item.to} className="flex items-center gap-2 text-base font-medium text-slate-700 hover:text-brand-red transition-colors"
                      data-testid={`nav-mobile-link-${slug(item.label)}`}>
                      {item.icon && <item.icon size={15} strokeWidth={2.2} />}
                      {item.label}
                    </Link>
                    <div className="flex flex-col gap-3 pl-4 border-l-2 border-brand-red/30">
                      {item.children.map((c) => (
                        <Link key={c.to} to={c.to} className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-brand-red transition-colors"
                          data-testid={`nav-mobile-${slug(c.label)}`}>
                          {c.icon && <c.icon size={14} strokeWidth={1.75} className="text-slate-400" />}
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link key={item.label} to={item.to} className="text-base font-medium text-slate-700 hover:text-brand-red transition-colors"
                    data-testid={`nav-mobile-link-${slug(item.label)}`}>
                    {item.label}
                  </Link>
                )
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
